import React, { useState } from 'react';
import { Building2, ChevronDown, Plus, Settings as SettingsIcon, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';

interface OrganizationSelectorProps {
  userEmail?: string;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({ userEmail }) => {
  const navigate = useNavigate();
  const { currentOrganization, organizations, isLoading, switchOrganization } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);

  if(!isLoading && !currentOrganization) {
    return null;
  } else if (!currentOrganization) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg animate-pulse">
        <Building2 className="h-4 w-4 text-gray-400" />
        <span className="text-sm text-gray-400">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Building2 className="h-4 w-4 text-blue-600" />
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium text-gray-900">{currentOrganization.name}</span>
          <span className="text-xs text-gray-500 capitalize">{currentOrganization.role}</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full mt-2 right-0 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
            {/* User Info */}
            {userEmail && (
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center space-x-2 text-sm">
                  <Mail className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-gray-700 font-medium">{userEmail}</span>
                </div>
              </div>
            )}

            {/* Current Organization Indicator */}
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {organizations.length > 1 ? 'Changer d\'Organisation' : 'Organisation'}
              </p>
            </div>

            {/* Organizations List */}
            <div className="max-h-64 overflow-y-auto">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => {
                    switchOrganization(org.id, '/dashboard');
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                    org.id === currentOrganization.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Building2 className={`h-4 w-4 ${
                      org.id === currentOrganization.id ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        org.id === currentOrganization.id ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {org.name}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">{org.role}</p>
                    </div>
                  </div>
                  {org.id === currentOrganization.id && (
                    <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                  )}
                </button>
              ))}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 mt-2">
              <button
                onClick={() => {
                  navigate('/organizations');
                  setIsOpen(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 text-blue-600"
              >
                <SettingsIcon className="h-4 w-4" />
                <span className="text-sm font-medium">Gérer les organisations</span>
              </button>
              
              <button
                onClick={() => {
                  navigate('/organizations/create');
                  setIsOpen(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 text-green-600"
              >
                <Plus className="h-4 w-4" />
                <span className="text-sm font-medium">Créer une organisation</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrganizationSelector;

